import { useMemo, useState } from 'react';

import { getCities, useCitiesQuery } from '@/api/getters';
import ControlledComboBox from '@/components/ui/ComboBox/ControlledCombobox';
import { FormControl, FormField, FormItem } from '@/components/ui/Form';
import { Input } from '@/components/ui/Input';
import {
  isValidZipCode,
  validateCity,
} from '@/features/v2/summaryForms/Validate';
import { EnumLike } from '@/types';

import { OWNER_ZIP_CODE_NAME } from './InputPostalCode';
import { useLingui } from '@lingui/react';
import debounce from 'lodash/debounce';
import { useFormContext } from 'react-hook-form';

export const OWNER_CITY_NAME = 'owner.city';
export const OWNER_CITY_COMPANY_NAME = 'owner.city_company';

const InputCity = ({
  name = OWNER_CITY_NAME,
  focused,
  setFocused,
  disabled,
  autoCompleteOff = false,
  controlled,
  prefix,
  displayValue,
  resetAddressOnChange = true,
}: {
  name: string;
  focused: string;
  setFocused: (value: string) => void;
  disabled?: boolean;
  autoCompleteOff?: boolean;
  controlled?: boolean;
  prefix?: string;
  displayValue?: (label: string) => string;
  // Off where the address does not depend on the city (free-text address).
  resetAddressOnChange?: boolean;
}) => {
  const { i18n } = useLingui();
  const { trigger, watch, setValue } = useFormContext();
  const [query, setQuery] = useState('');

  const postalCode = watch(prefix ? `${prefix}.zip_code` : OWNER_ZIP_CODE_NAME);

  const cities = useCitiesQuery(query, postalCode, !!query && query.length > 2);

  const triggerRefetch = () => {
    setTimeout(() => cities.refetch(), 10);
  };

  const changeHandler = (val: string) => {
    setQuery(val);
    if (val.length > 2 || val === '') {
      triggerRefetch();
    }
  };
  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, !!query && query.length > 4 ? 800 : 200),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query]
  );

  const resetAddress = () => {
    setValue(`${prefix}.address`, '');
    setValue(`${prefix}.without_street`, false);
  };

  if (controlled) {
    return (
      <FormField
        name={name}
        disabled={disabled}
        rules={{
          validate: async (value: EnumLike) => {
            if (
              !value ||
              !/^\d+$/.test(String(value.value)) ||
              !value.label ||
              !isValidZipCode(postalCode)
            ) {
              return i18n.t(
                'Wybierz miejscowość z listy zgodną z kodem pocztowym'
              );
            }
            try {
              const options = await getCities('', postalCode, value.value);
              return (
                options.some(
                  (option) => String(option.value) === String(value.value)
                ) ||
                i18n.t('Wybierz miejscowość z listy zgodną z kodem pocztowym')
              );
            } catch {
              return i18n.t(
                'Nie udało się sprawdzić miejscowości. Spróbuj ponownie'
              );
            }
          },
        }}
        defaultValue={''}
        render={({ field, fieldState }) => {
          const { onChange, onBlur, ...restField } = field;

          return (
            <FormItem className="col-span-4 md:col-span-3">
              <FormControl>
                <ControlledComboBox
                  field={{
                    ...field,
                  }}
                  options={
                    cities.data && cities.data?.length > 0
                      ? cities.data
                      : field?.value
                        ? [field.value]
                        : []
                  }
                  label={i18n.t('Miejscowość')}
                  query={query}
                  setQuery={(text) => {
                    if (text && text !== field.value?.label) {
                      onChange(null);
                      if (resetAddressOnChange) resetAddress();
                    }
                    debouncedChangeHandler(text);
                  }}
                  disabled={disabled}
                  error={fieldState.error?.message}
                  success={
                    focused !== field.name && !fieldState.error && field.value
                  }
                  onFocus={() => {
                    setFocused(field.name);

                    if (cities.data?.length === 0 && query === '') {
                      triggerRefetch();
                    }
                  }}
                  onChange={(e) => {
                    onChange(e);
                    if (controlled && resetAddressOnChange) {
                      resetAddress();
                    }
                  }}
                  onBlur={() => {
                    onBlur();
                    setFocused('');
                  }}
                  onFocusOut={() => {
                    setFocused('');
                  }}
                  noResultsLabel="Brak wyników, popraw lub sprawdź kod pocztowy"
                  displayValue={displayValue}
                  {...restField}
                />
              </FormControl>
            </FormItem>
          );
        }}
      />
    );
  }

  return (
    <FormField
      name={name}
      rules={{
        validate: (value: string) => validateCity({ label: value, value }),
      }}
      render={({ field, fieldState }) => {
        const { onChange, onBlur, ...restField } = field;

        return (
          <FormItem className="col-span-4 md:col-span-3">
            <FormControl>
              <Input
                autoComplete={autoCompleteOff ? 'none' : 'address-level2'}
                label={i18n.t('Miejscowość')}
                disabled={disabled}
                error={fieldState.error?.message}
                success={
                  focused !== field.name && !fieldState.error && field.value
                }
                onChange={(e) => {
                  onChange(e);
                  if (fieldState.error) {
                    trigger(field.name);
                  }
                }}
                onBlur={() => {
                  setFocused('');
                  onBlur();
                }}
                onFocus={() => setFocused(field.name)}
                {...restField}
              />
            </FormControl>
          </FormItem>
        );
      }}
    />
  );
};

export default InputCity;
